import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { Switch } from "@app/ui";
import { useT } from "../i18n.js";

/**
 * "Show demo data" for the platform console. Demo tenants (the showcase İnci
 * group) and demo accounts are hidden from platform stats and lists by default
 * so real-customer numbers stay honest; the operator can reveal them on demand.
 * A per-viewer convenience, so it lives in localStorage (wrapped — storage can
 * be unavailable) and is shared across console pages through context.
 */

const KEY = "idpp.platform.showDemo";

function read(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

interface ShowDemoState {
  showDemo: boolean;
  setShowDemo: (v: boolean) => void;
}

const Ctx = createContext<ShowDemoState | null>(null);

export function ShowDemoProvider({ children }: { children: ReactNode }) {
  const [showDemo, set] = useState(read);
  const value = useMemo<ShowDemoState>(
    () => ({
      showDemo,
      setShowDemo: (v) => {
        set(v);
        try {
          localStorage.setItem(KEY, v ? "1" : "0");
        } catch {
          /* storage unavailable — the choice lasts for this session only */
        }
      },
    }),
    [showDemo],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useShowDemo(): ShowDemoState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useShowDemo must be used within <ShowDemoProvider>");
  return ctx;
}

/** The toggle itself, placed in each console page's header. */
export function ShowDemoToggle() {
  const t = useT();
  const { showDemo, setShowDemo } = useShowDemo();
  return (
    <Switch checked={showDemo} onChange={(e) => setShowDemo(e.target.checked)}>
      {t("platform.showDemo")}
    </Switch>
  );
}
